import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { ChevronLeft, ChevronRight, Clock, CheckCircle2, MapPin } from "lucide-react";
import { toast } from "sonner";
import { FadeUp, Eyebrow } from "./motion";
import { API } from "../lib/site";
import PhoneInput from "./PhoneInput";
import { getUtm } from "../lib/utm";

const MONTHS = ["January", "February", "March", "April", "May", "June", "July",
  "August", "September", "October", "November", "December"];
const WEEKDAYS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

const toISO = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

const EMPTY_FORM = { name: "", phone: "+971 ", whatsapp: "+971 ", email: "", address: "", apartment: "", windows_count: "", message: "" };

export default function Booking() {
  const today = useMemo(() => new Date(), []);
  const [monthOffset, setMonthOffset] = useState(0);
  const [selected, setSelected] = useState(null);
  const [slots, setSlots] = useState([]);
  const [slotId, setSlotId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [gtagCfg, setGtagCfg] = useState(null);

  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/integrations/public`)
      .then((r) => r.json())
      .then(setGtagCfg)
      .catch(() => {});
  }, []);
  const [confirmed, setConfirmed] = useState(null);

  const viewDate = new Date(today.getFullYear(), today.getMonth() + monthOffset, 1);
  const year = viewDate.getFullYear();
  const month = viewDate.getMonth();
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const todayMid = new Date(today.getFullYear(), today.getMonth(), today.getDate());

  useEffect(() => {
    if (!selected) return;
    axios
      .get(`${API}/bookings/slots`, { params: { date: toISO(selected) } })
      .then((res) => setSlots(res.data.slots || []))
      .catch(() => setSlots([]));
  }, [selected]);

  const pickDay = (day) => {
    const d = new Date(year, month, day);
    if (d < todayMid) return;
    setSelected(d);
    setSlotId(null);
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const canSubmit =
    selected && slotId && form.name.trim() && form.address.trim() &&
    form.phone.replace(/\D/g, "").length >= 12;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) {
      toast.error("Please pick a date, a time slot and fill the required fields.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/bookings`, {
        name: form.name.trim(),
        phone: form.phone.trim(),
        whatsapp: form.whatsapp.trim() || undefined,
        email: form.email.trim() || undefined,
        address: form.address.trim(),
        apartment: form.apartment.trim() || undefined,
        windows_count: form.windows_count ? parseInt(form.windows_count) : undefined,
        message: form.message.trim() || undefined,
        date: toISO(selected),
        slot_id: slotId,
        ...(getUtm() ? { utm: getUtm() } : {}),
      });
      setConfirmed(res.data);
      toast.success("Free visit booked!");
      if (window.gtag) {
        window.gtag("event", "booking_submitted", {
          event_category: "booking",
          event_label: res.data.booking?.slot_label || "",
        });
        if (gtagCfg?.gads?.enabled && gtagCfg.gads.booking_label) {
          window.gtag("event", "conversion", {
            send_to: `${gtagCfg.gads.id}/${gtagCfg.gads.booking_label}`,
          });
        }
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Booking failed. Please try again.");
      if (err.response?.status === 409 && selected) {
        const res = await axios.get(`${API}/bookings/slots`, { params: { date: toISO(selected) } });
        setSlots(res.data.slots || []);
        setSlotId(null);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls =
    "mt-1.5 w-full rounded-xl border border-sand-border bg-white px-4 py-3 text-slate-900 focus:outline-none focus:ring-2 focus:ring-reef/40 focus:border-reef transition";
  const labelCls = "font-mono text-[10px] tracking-[0.18em] uppercase text-slate-500";

  return (
    <section id="book" data-testid="booking-section" className="py-20 sm:py-28 lg:py-32">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
        <FadeUp className="text-center max-w-2xl mx-auto">
          <Eyebrow className="mb-4 justify-center">Free doorstep visit</Eyebrow>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight text-slate-900">
            Book your <span className="italic text-reef">free visit.</span>
          </h2>
          <p className="mt-5 text-base sm:text-lg text-slate-600 leading-relaxed">
            Schedule a free consultation — measurements, fabric samples and design guidance,
            at zero cost, anywhere in Dubai.
          </p>
        </FadeUp>

        {confirmed ? (
          <FadeUp className="mt-14 max-w-xl mx-auto">
            <div data-testid="booking-success" className="bg-white border border-sand-border rounded-3xl p-8 sm:p-10 text-center">
              <CheckCircle2 className="w-16 h-16 text-reef mx-auto" strokeWidth={1.4} />
              <h3 className="mt-5 font-serif text-3xl text-slate-900">You're booked!</h3>
              <p className="mt-2 font-mono text-sm text-reef tracking-[0.15em]">
                REF: {confirmed.reference}
              </p>
              <div className="mt-6 text-sm text-slate-600 space-y-1.5">
                <p>{confirmed.booking.date} — {confirmed.booking.slot_label}</p>
                <p className="flex items-center justify-center gap-1.5">
                  <MapPin className="w-3.5 h-3.5 text-reef" /> {confirmed.booking.address}
                </p>
              </div>
              <p className="mt-5 text-sm text-slate-600">
                We'll confirm on WhatsApp shortly. Our specialist arrives with fabric samples
                and measures every window — completely free.
              </p>
              <button
                data-testid="booking-another-btn"
                onClick={() => { setConfirmed(null); setForm(EMPTY_FORM); setSelected(null); setSlotId(null); }}
                className="mt-7 px-8 py-3.5 rounded-full border border-reef/30 text-reef font-mono text-xs tracking-[0.18em] uppercase hover:bg-reef-light transition-colors"
              >
                Book another visit
              </button>
            </div>
          </FadeUp>
        ) : (
          <div className="mt-14 grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
            <FadeUp className="lg:order-2">
              <div className="bg-white border border-sand-border rounded-3xl p-6 sm:p-8">
                <div className="flex items-center justify-between mb-6">
                  <button
                    data-testid="booking-prev-month-btn"
                    onClick={() => setMonthOffset((m) => Math.max(0, m - 1))}
                    disabled={monthOffset === 0}
                    className="p-2 rounded-full border border-sand-border text-reef disabled:opacity-30 hover:bg-reef-light transition-colors"
                    aria-label="Previous month"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <p className="font-serif text-2xl text-slate-900">{MONTHS[month]} {year}</p>
                  <button
                    data-testid="booking-next-month-btn"
                    onClick={() => setMonthOffset((m) => Math.min(2, m + 1))}
                    disabled={monthOffset === 2}
                    className="p-2 rounded-full border border-sand-border text-reef disabled:opacity-30 hover:bg-reef-light transition-colors"
                    aria-label="Next month"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>

                <div className="grid grid-cols-7 gap-1.5 text-center mb-2">
                  {WEEKDAYS.map((d) => (
                    <span key={d} className="font-mono text-[10px] tracking-[0.15em] uppercase text-slate-400 py-1">{d}</span>
                  ))}
                </div>
                <div className="grid grid-cols-7 gap-1.5" data-testid="booking-date-picker">
                  {Array.from({ length: firstDay }).map((_, i) => (
                    <span key={`e${i}`} />
                  ))}
                  {Array.from({ length: daysInMonth }).map((_, i) => {
                    const day = i + 1;
                    const d = new Date(year, month, day);
                    const isPast = d < todayMid;
                    const isSel = selected && d.getTime() === new Date(selected.getFullYear(), selected.getMonth(), selected.getDate()).getTime();
                    return (
                      <button
                        key={day}
                        type="button"
                        data-testid={`booking-day-${day}`}
                        disabled={isPast}
                        onClick={() => pickDay(day)}
                        className={`aspect-square rounded-xl text-sm font-medium transition-all duration-200 ${
                          isSel
                            ? "bg-reef text-sand-bg shadow-lg"
                            : isPast
                              ? "text-slate-300 cursor-not-allowed"
                              : "text-slate-700 hover:bg-reef-light hover:text-reef"
                        }`}
                      >
                        {day}
                      </button>
                    );
                  })}
                </div>

                <div className="mt-7 border-t border-sand-border pt-6">
                  <p className="font-mono text-[10px] tracking-[0.18em] uppercase text-slate-500 flex items-center gap-2 mb-3.5">
                    <Clock className="w-3.5 h-3.5 text-reef" />
                    Time slot for the visit *
                  </p>
                  {!selected ? (
                    <p className="text-sm text-slate-400 italic">Pick a date above to see available slots.</p>
                  ) : (
                    <div className="grid grid-cols-3 sm:grid-cols-4 gap-2" data-testid="booking-slots">
                      {slots.map((s) => (
                        <button
                          key={s.id}
                          type="button"
                          data-testid={`booking-time-slot-${s.id}`}
                          disabled={s.booked}
                          onClick={() => setSlotId(s.id)}
                          className={`rounded-xl border px-2 py-2.5 text-center transition-all duration-200 ${
                            s.booked
                              ? "border-sand-border bg-sand-soft/60 text-slate-300 cursor-not-allowed"
                              : slotId === s.id
                                ? "border-reef bg-reef text-sand-bg"
                                : "border-sand-border hover:border-reef/50 text-slate-700"
                          }`}
                        >
                          <p className="text-[13px] font-semibold leading-tight">{s.label}</p>
                          {s.booked && (
                            <p className="font-mono text-[9px] mt-0.5 text-slate-400 uppercase tracking-[0.1em]">Booked</p>
                          )}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </FadeUp>

            <FadeUp delay={0.1} className="lg:order-1">
              <form onSubmit={submit} className="bg-white border border-sand-border rounded-3xl p-6 sm:p-8">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className={labelCls}>First name *</label>
                    <input data-testid="booking-name-input" value={form.name} onChange={set("name")} placeholder="Your name" className={inputCls} required />
                  </div>
                  <div>
                    <label className={labelCls}>Phone number *</label>
                    <PhoneInput testid="booking-phone-input" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} className={inputCls} required />
                  </div>
                  <div>
                    <label className={labelCls}>WhatsApp number</label>
                    <PhoneInput testid="booking-whatsapp-input" value={form.whatsapp} onChange={(v) => setForm({ ...form, whatsapp: v })} className={inputCls} />
                  </div>
                  <div>
                    <label className={labelCls}>Email (for confirmation)</label>
                    <input type="email" data-testid="booking-email-input" value={form.email} onChange={set("email")} placeholder="you@example.com" className={inputCls} />
                  </div>
                  <div>
                    <label className={labelCls}>Address *</label>
                    <input data-testid="booking-address-input" value={form.address} onChange={set("address")} placeholder="Building, street, area" className={inputCls} required />
                  </div>
                  <div>
                    <label className={labelCls}>Apartment / Villa no.</label>
                    <input data-testid="booking-apartment-input" value={form.apartment} onChange={set("apartment")} placeholder="Optional" className={inputCls} />
                  </div>
                  <div className="sm:col-span-2">
                    <label className={labelCls}>Number of windows</label>
                    <input type="number" min="1" max="100" data-testid="booking-windows-input" value={form.windows_count} onChange={set("windows_count")} placeholder="e.g. 4" className={inputCls} />
                  </div>
                  <div className="sm:col-span-2">
                    <label className={labelCls}>Message</label>
                    <textarea data-testid="booking-message-input" value={form.message} onChange={set("message")} rows={3} placeholder="Anything we should know?" className={`${inputCls} resize-none`} />
                  </div>
                </div>
                <button
                  type="submit"
                  data-testid="booking-submit-btn"
                  disabled={!canSubmit || submitting}
                  className="mt-6 w-full rounded-full bg-reef text-sand-bg py-4 font-mono text-xs tracking-[0.18em] uppercase hover:bg-reef-dark transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  {submitting ? "Booking…" : "Book a free visit"}
                </button>
                <p className="mt-3 text-center text-[11px] text-slate-400">
                  No payment, no obligation — the visit and measurement are 100% free.
                </p>
              </form>
            </FadeUp>
          </div>
        )}
      </div>
    </section>
  );
}
